<template>
  <div class="relative flex flex-grow items-start w-full overflow-hidden fade-in">
    <div class="absolute top-0 left-0 w-full h-full pointer-events-none">
      <div class="background-wrapper background-wrapper--step2">
        <BackgroundImage class="background" />
      </div>
    </div>
    <section class="relative w-full max-w-3xl mx-auto p-6 md:mt-16">
      <h1 class="uppercase text-2xl mb-4">Review subscription options</h1>
      <p class="mb-6">Subscribing is optional. Viewing these options does not create an invoice.</p>
      <p v-if="loading" role="status">Loading your prices...</p>
      <div v-else-if="!validPrices">
        <p role="alert" class="mb-4">
          We couldn’t load your subscription prices. No invoice has been created.
        </p>
        <Button type="secondary" @onClick="loadPrices">Try again</Button>
      </div>
      <template v-else>
        <h2 class="text-xl mb-2">{{ quote.org }}</h2>
        <p class="mb-6">Based on {{ quote.numberOfHosts.toLocaleString() }} hosts</p>
        <fieldset :disabled="submitting || submitted" class="mb-6">
          <legend class="mb-3">Choose a billing interval</legend>
          <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
            <label v-for="option in options" :key="option.period"
                   class="border p-4 cursor-pointer bg-black"
                   :class="selectedPeriod === option.period
                     ? 'border-orange-500' : 'border-gray-600'">
              <input type="radio" name="billing-period" :value="option.period"
                     v-model="selectedPeriod" class="mr-2">
              {{ option.label }}
              <span class="block mt-2">{{ money(quote[option.key]) }} {{ option.cadence }}</span>
            </label>
          </div>
        </fieldset>
        <div class="border border-gray-600 bg-black p-6">
          <p class="text-xl mb-3">{{ money(selectedAmount) }} USD {{ selectedOption.cadence }}</p>
          <template v-if="selectedAmount >= 50000">
            <p class="mb-4">Contact us to arrange this subscription.</p>
            <a :href="contactUrl" class="text-orange-500">Contact Orbital</a>
          </template>
          <template v-else>
            <p class="mb-4">
              Continue to payment creates a Square invoice for the selected subscription.
              You can review and pay it on Square. You have not been charged by viewing this page.
            </p>
            <Button type="primary" :inFlight="submitting" :disabled="submitted"
                    @onClick="confirmSubscription">Continue to payment</Button>
            <p v-if="submitting" role="status" class="mt-3">
              Creating your invoice. This can take up to 20 seconds.
            </p>
            <p v-if="paymentError" role="alert" class="mt-3">{{ paymentError }}</p>
          </template>
        </div>
      </template>
    </section>
  </div>
</template>
<script>
import BackgroundImage from '@/assets/new-report-bg.svg?inline';
import { mapActions } from 'vuex';
import Button from '@/components/Button.vue';

export default {
  name: 'Index',
  components: { BackgroundImage, Button },
  data() {
    return {
      loading: true,
      submitting: false,
      submitted: false,
      paymentError: '',
      quote: null,
      selectedPeriod: 12,
      options: [
        {
          period: 12, key: 'monthly', label: 'Monthly', cadence: 'per month',
        },
        {
          period: 4, key: 'quarterly', label: 'Quarterly', cadence: 'every 3 months',
        },
        {
          period: 2, key: 'sixMonth', label: 'Six month', cadence: 'every 6 months',
        },
        {
          period: 1, key: 'yearly', label: 'Annual', cadence: 'per year',
        },
      ],
    };
  },
  computed: {
    validPrices() {
      return this.quote && Number.isFinite(this.quote.numberOfHosts)
        && this.quote.numberOfHosts >= 0
        && this.options.every(({ key }) => Number.isFinite(this.quote[key]) && this.quote[key] > 0);
    },
    selectedOption() {
      return this.options.find(({ period }) => period === this.selectedPeriod);
    },
    selectedAmount() {
      return this.quote[this.selectedOption.key];
    },
    contactUrl() {
      return `mailto:orbital@phobos.io?subject=${encodeURIComponent(`${this.quote.org} subscription`)}`;
    },
  },
  async mounted() {
    const { token, period } = this.$route.query;
    if (this.options.some((option) => option.period === Number(period))) {
      this.selectedPeriod = Number(period);
    }
    const existingToken = localStorage.getItem('orbital-access-token');
    if (!existingToken && typeof token === 'string' && token) {
      localStorage.setItem('orbital-access-token', token);
    } else if (!existingToken) {
      await this.$router.push({ path: '/login' });
      return;
    }
    await this.loadPrices();
  },
  methods: {
    ...mapActions(['fetchPrices', 'getSubscriptionUrl']),
    money(amount) {
      return amount.toLocaleString('en-US', { style: 'currency', currency: 'USD' });
    },
    async loadPrices() {
      this.loading = true;
      try {
        this.quote = await this.fetchPrices({ openModal: false });
      } catch (error) {
        this.quote = null;
      } finally {
        this.loading = false;
      }
    },
    async confirmSubscription() {
      if (this.submitting || this.submitted || !this.validPrices
          || this.selectedAmount >= 50000) return;
      this.submitting = true;
      this.paymentError = '';
      try {
        this.submitted = await this.getSubscriptionUrl({ period: this.selectedPeriod });
        if (!this.submitted) this.paymentError = 'Unable to open payment. Please try again.';
      } catch (error) {
        this.paymentError = 'Unable to open payment. Please try again.';
      } finally {
        this.submitting = false;
      }
    },
  },
};
</script>
<style scoped>
.background {
  @apply max-w-screen-lg w-full h-full;
  animation: spin 120s linear infinite;
  transform-origin: center center;
}
.background-wrapper--step2 {
  transform: translateY(20%) scale(1);
}
</style>
